/* eslint-disable no-trailing-spaces */
import http from 'src/http/fetchClient';
import CONTENT_TYPES from './contentTypes';

class AccountService {
  changePassword = async (currentPassword, newPassword) => {
    try {
      return await http.put(
        '/api/admin/accounts/profile/password',
        { currentPassword, newPassword },
        {
          headers: {
            'Content-Type': CONTENT_TYPES.accounts_profile
          }
        }
      );
    } catch (error) {
      throw new Error(error.response?.data?.errorMessage || error.message || 'Something went wrong');
    }
  };

  getAdminUsers = async ({
    page,
    size,
    orderBy,
    order,
    providerId,
    roleId,
    status,
    type,
    searchTerm,
    partnerId,
    distributorId
  }) => {
    try {
      const response = await http.get('/api/admin/accounts/users', {
        headers: {
          'Content-Type': CONTENT_TYPES.accounts_adminUser
        },
        data: {},
        params: {
          page,
          size,
          orderBy,
          order,
          providerId,
          partnerId,
          distributorId,
          roleId,
          status,
          type,
          searchTerm
        }
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.errorMessage || error.message || 'Something went wrong');
    }
  };

  getAdminUser = async (id) => {
    try {
      const response = await http.get(`/api/admin/accounts/users/${id}`, {
        headers: {
          'Content-Type': CONTENT_TYPES.accounts_adminUser
        },
        data: {}
      });

      const providerRoles = [];
      if (response.data.roles) {
        response.data.roles.map((item) => {
          providerRoles.push({ providerId: item.providerId, roleId: item.role.id });
          return item;
        });
      }
      response.data.providerRoles = providerRoles;
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.errorMessage || error.message || 'Something went wrong');
    }
  };

  updateAdminUser = async ({
    id,
    firstName,
    lastName,
    email,
    status,
    providerRoles,
    partnerRoles,
    distributorRoles,
    type
  }) => {
    try {
      return await http.put(
        `/api/admin/accounts/users/${id}`,
        {
          firstName,
          lastName,
          email,
          status,
          providerRoles,
          partnerRoles,
          distributorRoles,
          type
        },
        {
          headers: {
            'Content-Type': CONTENT_TYPES.accounts_adminUser
          }
        }
      );
    } catch (error) {
      throw new Error(error.response?.data?.errorMessage || error.message || 'Something went wrong');
    }
  };

  resendInvitation = async (id) => {
    try {
      const response = await http.get(`/api/admin/accounts/users/invite/${id}`, {
        headers: {
          'Content-Type': CONTENT_TYPES.accounts_adminUser
        },
        data: {},
        params: {}
      });

      return response.data == null ? response : response.data;
    } catch (error) {
      throw new Error(error.response?.data?.errorMessage || error.message || 'Something went wrong');
    }
  };

  deleteAdminUser = async (id) => {
    try {
      return await http.delete(`/api/admin/accounts/users/${id}`, {
        headers: {
          'Content-Type': CONTENT_TYPES.accounts_adminUser
        },
        data: {}
      });
    } catch (error) {
      throw new Error(error.response?.data?.errorMessage || error.message || 'Something went wrong');
    }
  };

  invite = async ({ firstName, lastName, email, providerRoles, partnerRoles, distributorRoles, type }) => {
    try {
      return await http.post(
        '/api/admin/accounts/users/invite',
        {
          firstName,
          lastName,
          email,
          providerRoles,
          partnerRoles,
          distributorRoles,
          type
        },
        {
          headers: {
            'Content-Type': CONTENT_TYPES.accounts_adminUser
          }
        }
      );
    } catch (error) {
      throw new Error(error.response?.data?.errorMessage || error.message || 'Something went wrong');
    }
  };

  providerInvite = async ({ providerId, firstName, lastName, email, roleId, type }) => {
    const providerRoles = [{ providerId, roleId }];
    return this.invite({
      firstName,
      lastName,
      email,
      providerRoles,
      type
    });
  };

  partnerInvite = async ({ partnerId, firstName, lastName, email, roleId, type, status }) => {
    const partnerRoles = [{ partnerId, roleId }];
    return this.invite({
      firstName,
      lastName,
      email,
      partnerRoles,
      type,
      status
    });
  };

  distributorInvite = async ({ distributorId, firstName, lastName, email, roleId, type, status }) => {
    const distributorRoles = [{ distributorId, roleId }];
    return this.invite({
      firstName,
      lastName,
      email,
      distributorRoles,
      type,
      status
    });
  };

  getPermissions = async (page, order, orderBy, searchTerm, size, status, internal, external) => {
    try {
      const response = await http.get('/api/admin/accounts/permissions', {
        headers: {
          'Content-Type': CONTENT_TYPES.accounts_permission
        },
        data: {},
        params: {
          page,
          size,
          order,
          orderBy,
          searchTerm,
          status,
          internal,
          external
        }
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.errorMessage || error.message || 'Something went wrong');
    }
  };

  getPermission = async (id) => {
    try {
      const response = await http.get(`/api/admin/accounts/permissions/${id}`, {
        headers: {
          'Content-Type': CONTENT_TYPES.accounts_permission
        },
        data: {}
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.errorMessage || error.message || 'Something went wrong');
    }
  };

  updatePermission = async (id, name, status, internal, external) => {
    try {
      return await http.put(
        `/api/admin/accounts/permissions/${id}`,
        {
          name,
          status,
          internal,
          external
        },
        {
          headers: {
            'Content-Type': CONTENT_TYPES.accounts_permission
          }
        }
      );
    } catch (error) {
      throw new Error(error.response?.data?.errorMessage || error.message || 'Something went wrong');
    }
  };

  updateAccount = async (data) =>
    http.put('/api/admin/accounts/profile', data, {
      headers: {
        'Content-Type': CONTENT_TYPES.accounts_profile
      }
    });

  deleteCustomerPhone = async (id) => {
    try {
      return await http.delete(`/api/admin/accounts/customers/${id}/phone`, {
        headers: {
          'Content-Type': CONTENT_TYPES.accounts_customer
        },
        data: {}
      });
    } catch (error) {
      throw new Error(error.response?.data?.errorMessage || error.message || 'Something went wrong');
    }
  };
}

const accountService = new AccountService();

export default accountService;
